/**
 * NER-4485: mapeo entre el formulario plano de PromotionsPage y el shape
 * anidado que espera/devuelve el backend (`services/menu` promo-rules).
 *
 * Backend rule shape (list/create/update):
 *   { ruleId, name, type,
 *     schedule: { days: ['mon',...], start: 'HH:MM', end: 'HH:MM' },
 *     discount: { type: 'percentage'|'fixed', value: number },
 *     targets:  { categoryIds: [], itemIds: [] },
 *     status: 'active'|'inactive'|'deleted', createdAt, updatedAt }
 *
 * Form shape (estado local del componente):
 *   { name, type, days, startTime, endTime,
 *     discountType, discountValue, categoryIds, itemIds, isActive }
 */

export const EMPTY_FORM = {
  name: '',
  type: 'happy_hour',
  days: [],
  startTime: '00:00',
  endTime: '23:59',
  discountType: 'percentage',
  discountValue: 10,
  categoryIds: [],
  itemIds: [],
  isActive: true,
};

/**
 * Anidado (backend) -> plano (form). Usado al abrir el modal de edición.
 * @param {object} rule
 * @returns {object} form
 */
export function ruleToForm(rule) {
  if (!rule) return { ...EMPTY_FORM };
  const schedule = rule.schedule || {};
  const discount = rule.discount || {};
  const targets = rule.targets || {};
  return {
    name: rule.name || '',
    type: rule.type || 'happy_hour',
    days: Array.isArray(schedule.days) ? schedule.days : [],
    startTime: schedule.start || '00:00',
    endTime: schedule.end || '23:59',
    discountType: discount.type || 'percentage',
    discountValue: typeof discount.value === 'number' ? discount.value : 10,
    categoryIds: Array.isArray(targets.categoryIds) ? targets.categoryIds : [],
    itemIds: Array.isArray(targets.itemIds) ? targets.itemIds : [],
    isActive: rule.status === 'active',
  };
}

/**
 * Plano (form) -> anidado (backend). Usado al crear/actualizar.
 * No incluye `status` (el create lo fija en 'active'; el update no lo persiste
 * hoy — ver NER-4485 sub-issue backend). Usar `ruleToUpdateBody` para toggle.
 * @param {object} form
 * @returns {object} body
 */
export function formToRule(form) {
  return {
    name: (form.name || '').trim(),
    type: form.type,
    schedule: {
      days: Array.isArray(form.days) ? form.days : [],
      start: form.startTime,
      end: form.endTime,
    },
    discount: {
      type: form.discountType,
      value: Number(form.discountValue) || 0,
    },
    targets: {
      categoryIds: Array.isArray(form.categoryIds) ? form.categoryIds : [],
      itemIds: Array.isArray(form.itemIds) ? form.itemIds : [],
    },
  };
}

/**
 * Construye el body completo del PUT a partir de una regla del backend,
 * aplicando overrides (ej: `{ status }` para el toggle). El backend PUT
 * espera el shape anidado completo; enviamos `status` para que, una vez que
 * el handler lo persista (sub-issue backend NER-4485), el toggle funcione.
 * @param {object} rule - regla tal cual la devuelve el listado (anidada)
 * @param {object} [overrides]
 * @returns {object} body
 */
export function ruleToUpdateBody(rule, overrides = {}) {
  return {
    name: rule.name,
    type: rule.type,
    schedule: rule.schedule,
    discount: rule.discount,
    targets: rule.targets || { categoryIds: [], itemIds: [] },
    status: rule.status,
    ...overrides,
  };
}
